import Swal from 'sweetalert2';
import { sendDeleteRequest, sendPatchRequest, sendPostRequest } from './utils';
import { addAlert } from './alerts';

const customSwalClass = {
    container: 'modal',
    popup: 'modal__content',
    confirmButton: 'btn btn-submit',
    cancelButton: 'btn btn-secondary margin-right-10',
};

const reelectroPrompt = Swal.mixin({
    customClass: customSwalClass,
    buttonsStyling: false,
    backdrop: 'rgba(184, 211, 198, 0.8)',
    heightAuto: false,
});

export async function prompt({
    title = gettext('Potwierdź akcje'),
    text = '',
    html = '',
    confirmText = gettext('Potwierdź'),
    cancelText = gettext('Anuluj'),
    input = undefined,
    inputOptions = undefined,
    inputValue = undefined,
    inputLabel = undefined,
    showCancelButton = true,
    showConfirmButton = true,
    confirmAction = () => {},
    cancelAction = () => {},
    preConfirm = () => {},
    width = 600,
    customClass = {},
    didOpen = undefined,
} = {}) {
    const action = await reelectroPrompt.fire({
        title: title,
        text: text,
        html: html,
        width: width,
        showCancelButton: showCancelButton,
        showConfirmButton: showConfirmButton,
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        reverseButtons: true,
        input: input,
        inputOptions: inputOptions,
        inputValue: inputValue,
        inputLabel: inputLabel,
        backdrop: '#1b9ad680',
        background: '#F2F2EF',
        preConfirm: preConfirm,
        customClass: {
            ...customSwalClass,
            ...customClass,
        },
        didOpen: didOpen,
    });

    if (action.isConfirmed) {
        confirmAction(action);
    } else {
        cancelAction(action);
    }
    return action;
}

export function requestPrompt({
    options = {},
    method = null,
    url = null,
    data = null,
    successAction = function (response) {
        if (response?.data?.message) addAlert('success', response.data.message);
    },
    failedAction = function (response) {
        if (response?.data?.message) addAlert('error', response.data.message);
    },
}) {
    prompt({
        ...options,
        confirmAction: async function () {
            let func = null;
            switch (method) {
                case 'post':
                    func = sendPostRequest;
                    break;
                case 'patch':
                    func = sendPatchRequest;
                    break;
                case 'delete':
                    func = sendDeleteRequest;
                    break;
                default:
                    return;
            }
            try {
                const response = await func(url, data);
                if (response.ok) {
                    successAction(response);
                } else {
                    failedAction(response);
                }
            } catch (error) {
                const errorResponse = error.response || {};
                errorResponse.message = error.message;
                if (error.data) {
                    errorResponse.data = error.data;
                    errorResponse.json = async () => error.data;
                } else if (!errorResponse.json) {
                    errorResponse.json = async () => ({
                        message: error.message || gettext('Wystąpił błąd.'),
                    });
                }
                failedAction(errorResponse);
            }
        },
    });
}

export function postPrompt({
    options = undefined,
    url = undefined,
    data = undefined,
    successAction = undefined,
    failedAction = undefined,
}) {
    requestPrompt({
        options: options,
        method: 'post',
        url: url,
        data: data,
        successAction: successAction,
        failedAction: failedAction,
    });
}

export function patchPrompt({
    options = undefined,
    url = undefined,
    data = undefined,
    successAction = undefined,
    failedAction = undefined,
}) {
    requestPrompt({
        options: options,
        method: 'patch',
        url: url,
        data: data,
        successAction: successAction,
        failedAction: failedAction,
    });
}

export function deletePrompt({
    options = undefined,
    url = undefined,
    data = undefined,
    successAction = undefined,
    failedAction = undefined,
}) {
    requestPrompt({
        options: options,
        method: 'delete',
        url: url,
        data: data,
        successAction: successAction,
        failedAction: failedAction,
    });
}

export async function selectPrompt({
    options = undefined,
    choices = [],
    inputLabel = '',
}) {
    return await prompt({
        ...options,
        input: 'select',
        inputOptions: choices,
        inputLabel: inputLabel,
    });
}

export async function inputPrompt({
    options = undefined,
    inputValue = '',
    inputLabel = '',
}) {
    return await prompt({
        ...options,
        input: 'text',
        inputValue: inputValue,
        inputLabel: inputLabel,
    });
}

export async function textAreaPrompt({
    options = undefined,
    choices = [],
    inputValue = '',
    inputLabel = '',
}) {
    return await prompt({
        ...options,
        input: 'textarea',
        inputValue: inputValue,
        inputLabel: inputLabel,
    });
}

export function loaderPrompt(text, width = 600, customClass = {}) {
    reelectroPrompt.fire({
        title: text,
        html: `
            <div class="loader-container">            <div class="loader"></div>
</div>
        `,
        showConfirmButton: false,
        showCancelButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCloseButton: false,
        backdrop: '#1b9ad680',
        background: '#F2F2EF',
        width: width,
        customClass: customClass,
    });
}

export async function pausePrompt({ confirmAction = () => {} }) {
    const result = await reelectroPrompt.fire({
        title: gettext('Pauza'),
        html: gettext(
            'Wykryto brak aktywności.<br>Pomiar czasu został wstrzymany.',
        ),

        reverseButtons: true,

        showCancelButton: true,
        cancelButtonText: gettext('Wyjdź'),
        confirmButtonText: gettext('WZNÓW PRACĘ'),

        buttonsStyling: false,
        customClass: {
            confirmButton: 'btn btn-submit',
            cancelButton: 'btn btn-secondary margin-right-10',
            popup: 'modal__content',
            container: 'modal',
        },

        didOpen: (popup) => {
            const confirmBtn = popup.querySelector('.swal2-confirm');
            if (confirmBtn) {
                confirmBtn.style.border = 'none';
                confirmBtn.style.boxShadow = 'none';
            }
        },

        showCloseButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: 'info',
        width: 500,
    });

    if (result.isConfirmed) {
        confirmAction();
    } else if (result.dismiss === Swal.DismissReason.cancel) {
        window.location.href = '/';
    }
}

export function makeOpenDeleteModal(title) {
    return function (popup) {
        const header = document.createElement('div');
        header.className = 'window-header';
        header.innerHTML = `
            <span class="window-title">${title}</span>
            <div class="window-header-actions">
                <button type="button" class="window-close">
                    <i class="icon-close-bold"></i>
                </button>
            </div>
        `;
        popup.insertBefore(header, popup.firstChild);
        header
            .querySelector('.window-close')
            .addEventListener('click', () => Swal.close());

        const actions = popup.querySelector('.swal2-actions');
        const wrapper = document.createElement('div');
        wrapper.className = 'delete-confirm-checkbox';
        wrapper.innerHTML = `<label><input type="checkbox"> ${gettext('Akceptuję')}</label>`;
        actions.insertBefore(wrapper, actions.firstChild);

        const checkbox = wrapper.querySelector('input[type="checkbox"]');
        const confirmBtn = popup.querySelector('.swal2-confirm');
        confirmBtn.disabled = true;
        checkbox.addEventListener('change', () => {
            confirmBtn.disabled = !checkbox.checked;
        });
    };
}

export default {
    prompt,
    postPrompt,
    patchPrompt,
    deletePrompt,
    selectPrompt,
    inputPrompt,
    textAreaPrompt,
    loaderPrompt,
    pausePrompt,
};
